import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Check,
  FileCheck2,
  GripVertical,
  History,
  LockKeyhole,
  TriangleAlert,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useInterviewSession } from "../hooks/useInterviewSession";
import { useTypewriter } from "../hooks/useTypewriter";
import InterviewBriefing from "./InterviewBriefing";
import InterviewContextPanel, {
  interviewPhaseForQuestion,
  sourceStatementFromQuestion,
} from "./InterviewContextPanel";
import InterviewIntake from "./InterviewIntake";
import InterviewSheet from "./InterviewSheet";
import { Md, sanitizeStreamingMarkdown } from "./Md";
import { identitySpecFromInterview } from "../utils/identitySpec";
import { normalizeTranscript } from "../utils/transcript";

const OPENING_PROMPT = "State your current professional directions. Do not optimise. Just list what is true.";
const COMPACT_CONTEXT_QUERY = "(max-width: 960px)";

function storedDraft(interviewId) {
  try {
    return localStorage.getItem(`prime-portfolio-interview-draft:${interviewId}`) || "";
  } catch {
    return "";
  }
}

export function InterviewCompletionHandoff({ interview, identitySpec, packageData, onHistory, onOutputs }) {
  const availableArtifacts = packageData?.artifacts || [];
  const needsAttention = packageData?.status === "action_needed";

  return (
    <div className="completion-handoff" data-testid="interview-completion-handoff">
      <section className="completion-handoff-hero" aria-labelledby="completion-title">
        <div>
          <p className="context-label">Interview completed</p>
          <h1 id="completion-title">Interview complete.</h1>
          <p>Your answers are saved. Your identity and output package are ready below.</p>
        </div>
        <div className="completion-handoff-actions">
          <button className="primary-action" type="button" onClick={onOutputs} data-testid="review-ready-outputs-button">
            Review ready outputs <ArrowRight size={18} />
          </button>
          <button className="outline-button" type="button" onClick={onHistory} data-testid="completion-history-button">
            <History size={18} /> View interview history
          </button>
        </div>
      </section>

      <section className="completion-identity-strip" aria-label="Interview summary">
        <div className="completion-identity-source">
          <p className="context-label">Interview summary</p>
          <h2>{interview.title}</h2>
          <p>Your strongest career signals, carried consistently into every output.</p>
        </div>
        <dl>
          <div><dt>Role</dt><dd>{identitySpec.role || "Being established"}</dd></div>
          <div><dt>Focus</dt><dd>{identitySpec.focus || "Being established"}</dd></div>
          <div><dt>Edge</dt><dd>{identitySpec.edge || "Being established"}</dd></div>
        </dl>
      </section>

      <section className="completion-process" aria-label="Output preparation progress">
        <div className="completion-process-step is-complete"><span><Check size={18} /></span><div><strong>Interview captured</strong><p>Your answers have been saved.</p></div></div>
        <i aria-hidden="true" />
        <div className="completion-process-step is-complete"><span><Check size={18} /></span><div><strong>Identity synthesized</strong><p>Your core profile has been created.</p></div></div>
        <i aria-hidden="true" />
        <div className={`completion-process-step ${packageData && !needsAttention ? "is-complete" : "is-active"}`}><span>{packageData && !needsAttention ? <Check size={18} /> : <FileCheck2 size={18} />}</span><div><strong>Outputs prepared</strong><p>{needsAttention ? "One output needs a quick recovery step." : "Your output package is ready to review."}</p></div></div>
      </section>

      <section className="completion-package-preview" aria-labelledby="completion-package-title">
        <header>
          <div><p className="context-label">Output package</p><h2 id="completion-package-title">Your outputs</h2></div>
          {packageData && <span className={`output-package-status ${packageData.status}`}>{needsAttention ? <TriangleAlert size={15} /> : <Check size={15} />}{packageData.status_label}</span>}
        </header>
        {packageData ? (
          <div className="completion-artifact-list">
            {availableArtifacts.map((artifact) => (
              <div key={artifact.kind}><strong>{artifact.title}</strong><span>{artifact.status_label}</span></div>
            ))}
          </div>
        ) : <div className="loading-state">Preparing your output summary…</div>}
        <button className="output-text-action" type="button" onClick={onOutputs} data-testid="completion-open-package-button">Open full output package <ArrowRight size={16} /></button>
      </section>
    </div>
  );
}

export default function InterviewRoom() {
  const auth = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [message, setMessage] = useState(() => storedDraft(id));
  const [error, setError] = useState("");
  const [intakeDone, setIntakeDone] = useState(false);
  const [briefingDone, setBriefingDone] = useState(false);
  const [showBlob, setShowBlob] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [isCompact, setIsCompact] = useState(() => window.matchMedia(COMPACT_CONTEXT_QUERY).matches);
  const [contextOpen, setContextOpen] = useState(() => !window.matchMedia(COMPACT_CONTEXT_QUERY).matches);
  const [savedAt, setSavedAt] = useState(() => message ? Date.now() : null);
  const stageRef = useRef(null);
  const errorRef = useRef(null);
  const prevPhase = useRef("idle");

  const clearDraft = () => {
    try { localStorage.removeItem(`prime-portfolio-interview-draft:${id}`); } catch { /* storage can be unavailable */ }
  };

  const { phase, elapsed, send, cancel } = useInterviewSession({
    interviewId: id,
    authClient: auth.client,
    onReceived: (data) => {
      setInterview(data);
      setMessage("");
      setSavedAt(null);
      clearDraft();
      setError("");
    },
    onError: (msg) => setError(msg),
  });

  useEffect(() => {
    auth.client.get(`/interviews/${id}`).then(({ data }) => {
      setInterview(data);
      if (data.interview_type) setIntakeDone(true);
      if (data.status === "complete" || data.messages?.length > 0) setBriefingDone(true);
    });
  }, [auth.client, id]);

  useEffect(() => {
    if (interview?.status !== "complete") return;
    let active = true;
    auth.client.get(`/interviews/${id}/output-package`)
      .then(({ data }) => { if (active) setPackageData(data); })
      .catch(() => { if (active) setPackageData(null); });
    return () => { active = false; };
  }, [auth.client, id, interview?.status]);

  useEffect(() => {
    const media = window.matchMedia(COMPACT_CONTEXT_QUERY);
    const updateLayout = (event) => {
      setIsCompact(event.matches);
      setContextOpen(!event.matches);
    };
    media.addEventListener("change", updateLayout);
    return () => media.removeEventListener("change", updateLayout);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (message) localStorage.setItem(`prime-portfolio-interview-draft:${id}`, message);
        else localStorage.removeItem(`prime-portfolio-interview-draft:${id}`);
        setSavedAt(message ? Date.now() : null);
      } catch {
        setSavedAt(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [id, message]);

  useEffect(() => {
    if (prevPhase.current !== "idle" && phase === "idle" && stageRef.current) {
      stageRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    prevPhase.current = phase;
  }, [phase]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  const isBusy = phase === "submitting" || phase === "waiting" || phase === "recovering";
  const isSlowLabel = phase === "waiting" && elapsed > 30;
  const isCancelable = (phase === "waiting" && elapsed > 60) || phase === "recovering";

  const displayMessages = normalizeTranscript(interview?.messages);
  const assistantMessages = displayMessages.filter((item) => item.role === "assistant");
  const currentQuestion = assistantMessages[assistantMessages.length - 1];
  const questionNumber = Math.max(1, assistantMessages.length);
  const interviewPhase = interviewPhaseForQuestion(questionNumber);
  const identitySpec = identitySpecFromInterview(interview);
  const sourceStatement = sourceStatementFromQuestion(currentQuestion?.content);
  const resumeStatus = interview?.resume_ingestion?.status
    || (interview?.resume_context?.factual_anchors ? "ready" : "not_uploaded");

  const questionText = interview?.status !== "complete" && briefingDone && !showBlob
    ? (currentQuestion?.content ?? OPENING_PROMPT)
    : "";
  const { displayed, isTyping } = useTypewriter(questionText, { delay: currentQuestion ? 30 : 35 });
  const visibleQuestion = isTyping ? sanitizeStreamingMarkdown(displayed) : displayed;

  useEffect(() => {
    if (!briefingDone || currentQuestion) return;
    setShowBlob(true);
    const timer = setTimeout(() => setShowBlob(false), 5000);
    return () => clearTimeout(timer);
  }, [briefingDone, currentQuestion]);

  if (!interview) return <div className="loading-state" data-testid="interview-loading-state">Preparing interview room...</div>;
  if (!intakeDone) return <InterviewIntake interviewId={id} onComplete={async () => {
    const { data } = await auth.client.get(`/interviews/${id}`);
    setInterview(data);
    setIntakeDone(true);
  }} />;
  if (!briefingDone) return <InterviewBriefing onBegin={() => setBriefingDone(true)} interviewType={interview.interview_type} />;

  const historyMessages = currentQuestion
    ? displayMessages.filter((item) => item.id !== currentQuestion.id)
    : displayMessages;
  const answerCount = historyMessages.filter((item) => item.role === "user").length;
  const isComplete = interview.status === "complete";

  const submitAnswer = (event) => {
    event?.preventDefault();
    if (!message.trim() || isBusy) return;
    setError("");
    send(message.trim(), assistantMessages.length, displayMessages);
  };

  return (
    <section
      className={`interview-workspace ${contextOpen ? "context-is-open" : "context-is-closed"} ${isComplete ? "is-complete" : ""}`}
      data-testid="interview-room-page"
    >
      {!isComplete && isCompact && contextOpen && (
        <button
          type="button"
          className="context-backdrop"
          aria-label="Close question context"
          onClick={() => setContextOpen(false)}
          data-testid="context-backdrop"
        />
      )}

      {!isComplete && <aside
        id="interview-context-drawer"
        className="interview-context-drawer"
        aria-hidden={!contextOpen}
        data-testid="interview-context-drawer"
      >
        <InterviewContextPanel
          questionNumber={questionNumber}
          phase={interviewPhase}
          sourceStatement={sourceStatement}
          spec={identitySpec}
          isBusy={isBusy}
          resumeStatus={resumeStatus}
        />
      </aside>}

      {!isComplete && <button
        type="button"
        className="context-puller"
        onClick={() => setContextOpen((open) => !open)}
        aria-expanded={contextOpen}
        aria-controls="interview-context-drawer"
        aria-label={contextOpen ? "Hide question context" : "Show question context"}
        data-testid="context-drawer-toggle"
      >
        <GripVertical size={15} aria-hidden="true" />
        {contextOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>}

      <main className="interview-focus-panel" data-testid="chat-panel">
        <header className="interview-focus-header">
          <div className="interview-location" data-testid="interview-location">
            <strong>{isComplete ? "Interview complete" : `Question ${questionNumber}`}</strong>
            <span aria-hidden="true">·</span>
            <span>{isComplete ? "Output handoff" : interviewPhase.label}</span>
          </div>
          <div className="interview-header-actions">
            <button
              type="button"
              className="interview-header-button"
              onClick={() => setHistoryOpen(true)}
              data-testid="history-button"
            >
              <History size={18} /> History
              <span className="interview-action-count">{answerCount}</span>
            </button>
            {interview.status === "complete" && (
              <button
                type="button"
                className="interview-header-button"
                onClick={() => navigate(`/outputs/op-${interview.id}`)}
                data-testid="outputs-button"
              >
                <FileCheck2 size={18} /> Outputs
              </button>
            )}
          </div>
        </header>

        <div className={`interview-focus-scroll ${isComplete ? "is-complete" : ""}`}>
          {isComplete ? (
            <InterviewCompletionHandoff
              interview={interview}
              identitySpec={identitySpec}
              packageData={packageData}
              onHistory={() => setHistoryOpen(true)}
              onOutputs={() => navigate(`/outputs/op-${interview.id}`)}
            />
          ) : <>
          <section className="interview-question-stage" ref={stageRef} data-testid="interrogation-stage" aria-busy={isBusy}>
            <p className="context-label" data-testid="current-question-label">Active question</p>
            {showBlob ? (
              <div className="question-blob" data-testid="question-blob" aria-label="Loading first question" />
            ) : (
              <div
                className={`interview-question-copy ${currentQuestion ? "derived-question" : "opening-question"}`}
                data-testid="current-question-text"
              >
                <Md>{visibleQuestion}</Md>
                {isTyping && <span className="type-cursor" aria-hidden="true" />}
              </div>
            )}

            {isBusy && (
              <div className="interview-thinking" data-testid="thinking-animation" role="status" aria-live="polite" aria-atomic="true">
                <span className="thinking-spinner" aria-hidden="true" />
                <div>
                  <strong>{phase === "submitting" ? "Saving your answer" : phase === "recovering" ? "Still preparing your next question" : isSlowLabel ? "Still working" : "Preparing your next question"}</strong>
                  <p>
                    {phase === "recovering"
                      ? "The server is taking longer than expected. Your answer is saved, and this page will keep checking."
                      : phase === "submitting"
                        ? "Please keep this page open for a moment."
                        : isSlowLabel
                          ? "This is taking longer than usual. Your answer is saved."
                          : "Your answer is saved. We’re connecting it with the rest of your interview."}
                  </p>
                </div>
                {isSlowLabel && <span className="elapsed-timer" data-testid="elapsed-timer" aria-hidden="true">{elapsed}s</span>}
                {isCancelable && (
                  <button type="button" className="interview-cancel-button" onClick={cancel} data-testid="cancel-send-button">
                    Stop waiting
                  </button>
                )}
              </div>
            )}
          </section>

          {error && <div ref={errorRef} className="error-note" data-testid="interview-error-message" role="alert" tabIndex={-1}>{error}</div>}

            <form className="interview-composer" onSubmit={submitAnswer} data-testid="message-composer-form" aria-busy={isBusy}>
              <label htmlFor="interview-answer">Your answer</label>
              <textarea
                id="interview-answer"
                data-testid="message-input"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") submitAnswer(event);
                }}
                placeholder="Answer in your own words. Specifics are more useful than polish."
                readOnly={isBusy}
              />
              <footer className="interview-composer-footer">
                <div className="composer-status-group">
                  <span><Clock3 size={15} /> {savedAt ? "Draft saved in this browser" : "Draft saves as you type"}</span>
                  <span><LockKeyhole size={15} /> Your answer is private and used only to inform your portfolio.</span>
                </div>
                <button className="primary-action" type="submit" disabled={isBusy || !message.trim()} data-testid="send-message-button">
                  {phase === "submitting" ? "Sending…" : phase === "error" ? "Try again" : "Submit signal"}
                  <ArrowRight size={18} />
                </button>
              </footer>
            </form>
          </>}
        </div>
      </main>

      <InterviewSheet open={historyOpen} onOpenChange={setHistoryOpen} title="Interview history" testId="interview-history-sheet">
        <div className="interview-history-list" data-testid="message-stream">
          {!historyMessages.length && <p className="empty-state">Your previous questions and answers will appear here.</p>}
          {historyMessages.map((item, index) => (
            <article className={`history-message ${item.role}`} key={item.id || `${item.role}-${index}`} data-testid={`message-${item.id || index}`}>
              <span>{item.role === "user" ? "Your answer" : "Question"}</span>
              <Md>{item.content}</Md>
            </article>
          ))}
        </div>
      </InterviewSheet>

    </section>
  );
}
